import React from 'react';
import { useIntl } from 'react-intl';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { OutreachShell } from '../components/OutreachShell';
import { PlugBar } from '../components/PlugBar';
import { PlugWorking } from '../components/PlugWorking';
import { useOutreachProfile } from '../hooks/useOutreachProfile';
import { useProfileActions } from '../hooks/useProfileActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { outreachMessages } from '../outreachMessages';
import { plugMessages } from '../plugMessages';
import { shortDate } from '../outreachFormat';
import { ProfileGrid } from './ProfileGrid';
import { ProfileStateBar } from './ProfileStateBar';
import { profileMessages as messages } from './profileMessages';

/**
 * Your sound (design outreach b1 to b3): genres and moods, similar artists with their source,
 * markets, size, bio and story, and the sender line. Every change saves at once. While Plug builds
 * the profile the working state shows (b2); the realtime event ends it.
 */
const SoundProfileScreenComponent: React.FC<{ accountId: number }> = ({ accountId }) => {
  const intl = useIntl();
  const { isDemo } = useOutreachPlan();
  const { profile, isLoading, error, retry } = useOutreachProfile(accountId);
  const actions = useProfileActions(accountId);

  const rebuild = () => actions.startBuild(null);

  const content = () => {
    if (isLoading) {
      return <LoadingView />;
    }
    if (error !== null || profile === undefined) {
      return <ErrorView error={error ?? undefined} retry={retry} />;
    }
    if (profile === null) {
      return (
        <PlugBar title={intl.formatMessage(messages.noProfileTitle)} sub={intl.formatMessage(messages.noProfileBody)}>
          <SMLButton
            color="primary"
            size="sm"
            omitCap
            onClick={rebuild}
            isLoading={actions.isBuilding}
            disabled={isDemo}
          >
            {intl.formatMessage(plugMessages.buildProfile)}
          </SMLButton>
        </PlugBar>
      );
    }
    if (profile.status === 'building') {
      return (
        <PlugWorking
          title={intl.formatMessage(plugMessages.working)}
          sub={intl.formatMessage(plugMessages.workingSub)}
        />
      );
    }
    return (
      <>
        <ProfileStateBar accountId={accountId} showReady />
        {actions.saveError !== null && (
          <p className="text-danger small mb-0">{intl.formatMessage(messages.saveFailed)}</p>
        )}
        <ProfileGrid profile={profile} onSave={actions.save} disabled={isDemo} />
      </>
    );
  };

  return (
    <OutreachShell
      section="under"
      title={intl.formatMessage(messages.title)}
      subtitle={intl.formatMessage(messages.intro)}
      crumbs={[intl.formatMessage(outreachMessages.crumbPitch), intl.formatMessage(messages.title)]}
      actions={
        profile !== null && profile !== undefined && profile.status !== 'building' ? (
          <>
            <span className="small text-body-secondary">
              {actions.isSaving
                ? intl.formatMessage(messages.saving)
                : intl.formatMessage(messages.updated, { date: shortDate(intl, profile.updatedAt) })}
            </span>
            <SMLButton size="sm" outline omitCap leftIcon={faArrowsRotate} onClick={rebuild} disabled={isDemo}>
              {intl.formatMessage(messages.refresh)}
            </SMLButton>
          </>
        ) : undefined
      }
    >
      {content()}
    </OutreachShell>
  );
};

export const SoundProfileScreen = TypedMemo(SoundProfileScreenComponent);
